<script lang="ts">
  import { onMount } from "svelte";
  import { push, link } from "svelte-spa-router";
  import {
    getUser,
    listRooms,
    listSchools,
    listAuthorities,
    updateUser,
    deleteUser,
    addUserRole,
    removeUserRole,
    sendUserReset,
    resendUserVerification,
    setRoomStudents,
    setUserLogLevel,
    setUserProfileImage,
    fetchStudentProfileImage,
    ApiError,
  } from "../lib/api";
  import Breadcrumb from "../lib/Breadcrumb.svelte";
  import { scopes } from "../lib/session.svelte";
  import {
    LOG_LEVELS,
    type AdminUser,
    type AdminRoom,
    type School,
    type Authority,
    type RoleGrant,
    type LogLevel,
  } from "@diy-avatar/shared/admin";
  import type { Locale, Role } from "@diy-avatar/shared/api";

  export let params: { id?: string } = {};
  $: userId = params.id ? parseInt(params.id, 10) : NaN;

  let user: AdminUser | null = null;
  let rooms: AdminRoom[] = [];
  let schools: School[] = [];
  let authorities: Authority[] = [];
  let profileImage: string | null = null;

  let loading = false;
  let saving = false;
  let error: string | null = null;
  let notice: string | null = null;
  let notFound = false;

  // Editable profile fields.
  let displayName = "";
  let fullName = "";
  let language: Locale = "de";
  let disabled = false;
  let maxSessions = 1;
  let logLevel: LogLevel = "info";
  // roomId -> membership checkbox state for this user.
  let roomMembership: Record<number, boolean> = {};

  // Password field.
  let newPassword = "";

  function errText(err: unknown, fallback: string): string {
    if (err instanceof ApiError) {
      if (err.message === "last_school_manager") {
        return "Cannot remove the last school-manager of a school.";
      }
      return err.message;
    }
    return err instanceof Error ? err.message : fallback;
  }

  function handleAuth(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) {
      push("/");
      return true;
    }
    return false;
  }

  function schoolName(id: number | null | undefined): string {
    if (id == null) return "—";
    return schools.find((s) => s.id === id)?.name ?? `school ${id}`;
  }

  // Schools the user holds a role in — scope which rooms are assignable.
  function userSchoolIds(): Set<number> {
    const out = new Set<number>();
    if (!user) return out;
    for (const r of user.roles) {
      if (r.school_id != null) out.add(r.school_id);
    }
    return out;
  }

  function hasSchoolRole(): boolean {
    return userSchoolIds().size > 0;
  }

  function assignableRooms(): AdminRoom[] {
    const ids = userSchoolIds();
    if (ids.size === 0) return [];
    return rooms.filter((r) => ids.has(r.school_id));
  }

  function roleChip(r: AdminUser["roles"][number]): string {
    if (r.role === "admin") return "admin";
    if (r.role === "authority") return `authority@${r.authority_name ?? r.authority_id}`;
    return `${r.role}@${r.school_name ?? r.school_id}`;
  }

  function syncFromUser() {
    if (!user) return;
    displayName = user.display_name ?? "";
    fullName = user.full_name ?? "";
    language = user.language;
    disabled = user.disabled;
    maxSessions = user.max_sessions ?? 1;
    logLevel = user.loglevel;
    const map: Record<number, boolean> = {};
    for (const r of rooms) map[r.id] = r.students.includes(user.email);
    roomMembership = map;
  }

  async function loadAvatar() {
    if (user && user.has_profile_image) {
      try {
        profileImage = await fetchStudentProfileImage(String(user.id));
      } catch {
        profileImage = null;
      }
    } else {
      profileImage = null;
    }
  }

  async function load(id: number) {
    if (!Number.isFinite(id)) return;
    loading = true;
    error = null;
    notFound = false;
    try {
      const res = await getUser(id);
      user = res.user;
    } catch (err) {
      if (handleAuth(err)) return;
      if (
        (err instanceof ApiError && err.status === 404) ||
        (err instanceof ApiError &&
          (err.message === "not_found" || err.message === "not-found"))
      ) {
        notFound = true;
        user = null;
      } else {
        error = errText(err, "Could not load user.");
      }
      loading = false;
      return;
    }

    const [rm, sc, au] = await Promise.all([
      listRooms().catch(() => ({ rooms: [] as AdminRoom[] })),
      listSchools().catch(() => ({ schools: [] as School[] })),
      $scopes.isAdmin
        ? listAuthorities().catch(() => ({ authorities: [] as Authority[] }))
        : Promise.resolve({ authorities: [] as Authority[] }),
    ]);
    rooms = rm.rooms;
    schools = sc.schools;
    authorities = au.authorities;
    syncFromUser();
    loading = false;
    await loadAvatar();
  }

  async function reload() {
    if (Number.isFinite(userId)) await load(userId);
  }

  // ---- profile save ----
  async function save() {
    if (!user) return;
    error = null;
    notice = null;
    saving = true;
    try {
      // Clamp max sessions to the backend-allowed range (1..50).
      const sessions = Math.min(50, Math.max(1, Math.round(maxSessions || 1)));
      maxSessions = sessions;

      await updateUser(user.id, {
        display_name: displayName.trim() || null,
        full_name: fullName.trim() || null,
        language,
        disabled,
        max_sessions: sessions,
      });

      if (logLevel !== user.loglevel) {
        await setUserLogLevel(user.id, logLevel);
      }

      // Room membership: diff against current state, push per affected room.
      const email = user.email;
      for (const r of rooms) {
        const want = !!roomMembership[r.id];
        const has = r.students.includes(email);
        if (want === has) continue;
        const next = want
          ? [...r.students, email]
          : r.students.filter((e) => e !== email);
        await setRoomStudents(r.id, next);
      }

      notice = "Saved.";
      await reload();
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "save failed");
    } finally {
      saving = false;
    }
  }

  // ---- avatar ----
  async function onAvatarFile(ev: Event) {
    if (!user) return;
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    error = null;
    notice = null;
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error ?? new Error("read failed"));
        reader.readAsDataURL(file);
      });
      await setUserProfileImage(user.id, { image: dataUrl });
      notice = "Avatar updated.";
      await reload();
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "avatar upload failed");
    } finally {
      // Allow re-selecting the same file later.
      input.value = "";
    }
  }

  async function clearAvatar() {
    if (!user) return;
    error = null;
    notice = null;
    try {
      await setUserProfileImage(user.id, { image: null });
      notice = "Avatar cleared.";
      await reload();
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "avatar clear failed");
    }
  }

  // ---- password / verification ----
  async function setPassword() {
    if (!user || !newPassword) return;
    // E2EE WARNING: an admin password reset rotates the password-derived
    // wrapping key WITHOUT the user's old password, DESTROYING their E2EE key.
    const ok = confirm(
      `Set a new password for ${user.email}?\n\n` +
        "WARNING: this DESTROYS the user's end-to-end-encryption key. " +
        "They will need to re-add their key (scan/paste) on next login. " +
        "Previously encrypted media stays unreadable until re-shared.\n\n" +
        "Continue?",
    );
    if (!ok) return;
    error = null;
    notice = null;
    try {
      await updateUser(user.id, { password: newPassword });
      newPassword = "";
      notice = "Password set. The user must re-add their encryption key on next login.";
      await reload();
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "set password failed");
    }
  }

  async function doSendReset() {
    if (!user) return;
    error = null;
    notice = null;
    try {
      await sendUserReset(user.id);
      notice = `Password-reset link sent to ${user.email}.`;
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "send reset failed");
    }
  }

  async function doResendVerification() {
    if (!user) return;
    error = null;
    notice = null;
    try {
      await resendUserVerification(user.id);
      notice = `Verification email sent to ${user.email}.`;
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "resend failed");
    }
  }

  // ---- roles (inline add/remove, ported from Users.svelte) ----
  let addingRole = false;
  let addRoleSel: Role = "teacher";
  let addSchoolSel: number | "" = "";
  let addAuthoritySel: number | "" = "";

  function openAddRole() {
    addingRole = true;
    addRoleSel = "teacher";
    addSchoolSel = schools.length ? schools[0].id : "";
    addAuthoritySel = authorities.length ? authorities[0].id : "";
  }

  function cancelAddRole() {
    addingRole = false;
  }

  function buildAddGrant(): RoleGrant | null {
    if (addRoleSel === "admin") return { role: "admin" };
    if (addRoleSel === "authority") {
      if (addAuthoritySel === "") return null;
      return { role: "authority", authority_id: addAuthoritySel };
    }
    if (addSchoolSel === "") return null;
    return { role: addRoleSel, school_id: addSchoolSel };
  }

  async function confirmAddRole() {
    if (!user) return;
    const grant = buildAddGrant();
    if (!grant) {
      error = "pick a scope for the role";
      return;
    }
    error = null;
    notice = null;
    try {
      await addUserRole(user.id, grant);
      addingRole = false;
      await reload();
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "add role failed");
    }
  }

  async function dropRole(roleId: number) {
    if (!user) return;
    error = null;
    notice = null;
    try {
      await removeUserRole(user.id, roleId);
      await reload();
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "remove role failed");
    }
  }

  // ---- delete ----
  async function doDelete() {
    if (!user) return;
    if (!confirm(`Delete ${user.email}? This cannot be undone.`)) return;
    error = null;
    try {
      await deleteUser(user.id);
      push("/users");
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "delete failed");
    }
  }

  $: if (Number.isFinite(userId)) void load(userId);
  onMount(() => {});
</script>

<section>
  <Breadcrumb
    items={[
      { label: "User", href: "/users" },
      {
        label: user?.full_name ?? user?.display_name ?? user?.email ?? "…",
        href: user ? `/users/${user.id}` : "/users",
      },
      { label: "Edit" },
    ]}
  />
  <h1>Edit user</h1>

  {#if error}<p class="error" data-testid="error">{error}</p>{/if}
  {#if notice}<p class="notice" data-testid="notice">{notice}</p>{/if}

  {#if user}
    <div class="grid">
      <!-- Avatar -->
      <div class="card avatar-card" data-testid="edit-avatar">
        <h2>Avatar</h2>
        <div class="avatar-row">
          {#if profileImage}
            <img src={profileImage} alt="avatar" class="avatar" />
          {:else}
            <div class="avatar placeholder" aria-hidden="true">
              {(user.full_name ?? user.display_name ?? user.email)
                .slice(0, 1)
                .toUpperCase()}
            </div>
          {/if}
          <div class="avatar-actions">
            <label class="upload-btn">
              Upload image
              <input
                type="file"
                accept="image/*"
                on:change={onAvatarFile}
                data-testid="edit-avatar-upload"
              />
            </label>
            <button
              type="button"
              class="ghost"
              on:click={clearAvatar}
              data-testid="edit-avatar-clear"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      <!-- Profile fields -->
      <div class="card" data-testid="edit-profile">
        <h2>Profile</h2>
        <label class="field">
          Display name
          <input type="text" bind:value={displayName} data-testid="edit-display-name" />
        </label>
        <label class="field">
          Full name
          <input type="text" bind:value={fullName} data-testid="edit-full-name" />
        </label>
        <label class="field">
          Email <span class="ro">(read-only)</span>
          <input type="email" value={user.email} readonly data-testid="edit-email" />
        </label>
        <label class="field">
          Language
          <select bind:value={language} data-testid="edit-language">
            <option value="de">de</option>
            <option value="en">en</option>
          </select>
        </label>
        <label class="field check">
          <input type="checkbox" bind:checked={disabled} data-testid="edit-disabled" />
          Disabled
        </label>
        <label class="field">
          Max simultaneous logins
          <input
            type="number"
            min="1"
            max="50"
            step="1"
            bind:value={maxSessions}
            data-testid="edit-max-sessions"
          />
          <span class="ro">Max. gleichzeitige Logins (1–50).</span>
        </label>
        <label class="field">
          Log level
          <select bind:value={logLevel} data-testid="edit-loglevel">
            {#each LOG_LEVELS as lvl}
              <option value={lvl}>{lvl}</option>
            {/each}
          </select>
          <span class="ro">Effective log verbosity for this user.</span>
        </label>
        <div class="actions">
          <button on:click={save} disabled={saving} data-testid="edit-save">
            {saving ? "Saving…" : "Save"}
          </button>
          <a href="/users/{user.id}" use:link class="cancel">Cancel</a>
        </div>
      </div>

      <!-- Password & verification -->
      <div class="card" data-testid="edit-security">
        <h2>Password &amp; verification</h2>
        <label class="field">
          Set new password
          <input
            type="text"
            bind:value={newPassword}
            placeholder="new password"
            data-testid="edit-password"
          />
          <span class="ro">
            Setting a password DESTROYS the user's E2EE key (they must re-add it).
          </span>
        </label>
        <div class="actions">
          <button
            type="button"
            on:click={setPassword}
            disabled={!newPassword}
            data-testid="edit-set-password"
          >
            Set password
          </button>
          <button type="button" class="ghost" on:click={doSendReset} data-testid="edit-send-reset">
            Send reset link
          </button>
          {#if !user.email_verified}
            <button
              type="button"
              class="ghost"
              on:click={doResendVerification}
              data-testid="edit-resend-verification"
            >
              Resend verification
            </button>
          {/if}
        </div>
      </div>

      <!-- Roles -->
      <div class="card" data-testid="edit-roles">
        <h2>Roles</h2>
        <div class="chips">
          {#each user.roles as r (r.id)}
            <span class="chip" data-testid="role-chip-{r.id}">
              {roleChip(r)}
              <button
                class="chip-x"
                title="remove role"
                on:click={() => dropRole(r.id)}
                data-testid="drop-role-{r.id}">×</button
              >
            </span>
          {/each}
          {#if user.roles.length === 0}<span class="muted">no roles</span>{/if}
          {#if addingRole}
            <span class="chip add-role" data-testid="add-role-form">
              <select bind:value={addRoleSel} data-testid="add-role-select">
                {#if $scopes.isAdmin}<option value="admin">admin</option>{/if}
                {#if $scopes.isAdmin}<option value="authority">authority</option>{/if}
                <option value="school-manager">school-manager</option>
                <option value="teacher">teacher</option>
                <option value="student">student</option>
              </select>
              {#if addRoleSel === "authority"}
                <select bind:value={addAuthoritySel} data-testid="add-role-authority">
                  {#each authorities as a (a.id)}<option value={a.id}>{a.name}</option>{/each}
                </select>
              {:else if addRoleSel !== "admin"}
                <select bind:value={addSchoolSel} data-testid="add-role-school">
                  {#each schools as s (s.id)}<option value={s.id}>{s.name}</option>{/each}
                </select>
              {/if}
              <button
                class="chip-x"
                title="confirm"
                on:click={confirmAddRole}
                data-testid="add-role-confirm">✓</button
              >
              <button class="chip-x" title="cancel" on:click={cancelAddRole}>×</button>
            </span>
          {:else}
            <button
              class="chip add-role-toggle"
              on:click={openAddRole}
              data-testid="add-role-toggle">+ add role</button
            >
          {/if}
        </div>
      </div>

      <!-- Room assignments -->
      <div class="card" data-testid="edit-rooms">
        <h2>Room assignments</h2>
        {#if hasSchoolRole()}
          {#each assignableRooms() as r (r.id)}
            <label class="member">
              <input
                type="checkbox"
                bind:checked={roomMembership[r.id]}
                data-testid="edit-room-{r.id}"
              />
              {r.name}
              <span class="muted">· {schoolName(r.school_id)}</span>
            </label>
          {/each}
          {#if assignableRooms().length === 0}
            <span class="muted">no rooms available</span>
          {/if}
          <p class="ro">Room changes are applied on Save.</p>
        {:else}
          <span class="muted">no rooms available (user has no school role)</span>
        {/if}
      </div>

      <!-- Delete -->
      <div class="card danger" data-testid="edit-danger">
        <h2>Danger zone</h2>
        <p class="ro">Permanently delete this user account.</p>
        <button class="delete" on:click={doDelete} data-testid="edit-delete">
          Delete user
        </button>
      </div>
    </div>
  {:else if !loading}
    <p class="error" data-testid="not-found">User not found.</p>
  {/if}
</section>

<style>
  h1 { margin: 0 0 1rem; }
  h2 { margin: 0 0 0.5rem; font-size: 1rem; color: #34d399; }
  .grid {
    display: grid;
    gap: 0.75rem;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    align-items: start;
  }
  .card {
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 8px;
    padding: 1rem;
    display: grid;
    gap: 0.75rem;
  }
  .card.danger { border-color: #7f1d1d; }
  .field {
    display: grid;
    gap: 0.25rem;
    font-size: 0.85rem;
    color: #9ca3af;
  }
  .field.check {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    color: #d1d5db;
  }
  .ro { color: #6b7280; font-weight: 400; font-size: 0.8rem; margin: 0; }
  input, select {
    padding: 0.4rem 0.5rem;
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #1f2937;
    border-radius: 4px;
  }
  input[readonly] { opacity: 0.6; }
  .avatar-row { display: flex; align-items: center; gap: 1rem; }
  .avatar {
    width: 72px;
    height: 72px;
    border-radius: 8px;
    object-fit: cover;
    background: #0f172a;
    flex: none;
  }
  .avatar.placeholder {
    display: grid;
    place-items: center;
    color: #6ee7b7;
    font-size: 1.8rem;
    font-weight: 700;
    border: 1px solid #1f2937;
  }
  .avatar-actions { display: grid; gap: 0.5rem; }
  .upload-btn {
    display: inline-block;
    padding: 0.4rem 0.8rem;
    background: #1f2937;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.85rem;
  }
  .upload-btn input[type="file"] { display: none; }
  .member {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    color: #d1d5db;
    font-size: 0.85rem;
  }
  .actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
  }
  button {
    padding: 0.4rem 0.8rem;
    background: #1f2937;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 6px;
    cursor: pointer;
  }
  button:disabled { opacity: 0.5; cursor: not-allowed; }
  button.ghost { background: #0f172a; }
  button.delete {
    background: #7f1d1d;
    border-color: #991b1b;
    justify-self: start;
  }
  .cancel { color: #9ca3af; text-decoration: none; font-size: 0.85rem; }
  .cancel:hover { text-decoration: underline; }
  .chips { display: flex; flex-wrap: wrap; gap: 0.3rem; align-items: center; }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    background: #1f2937;
    color: #d1d5db;
    padding: 0.1rem 0.4rem;
    border-radius: 10px;
    font-size: 0.75rem;
  }
  .chip-x {
    background: none;
    border: none;
    color: #9ca3af;
    cursor: pointer;
    padding: 0;
    font-size: 0.9rem;
    line-height: 1;
  }
  .chip-x:hover { color: #fca5a5; }
  .add-role-toggle { cursor: pointer; border: 1px dashed #374151; }
  .add-role { gap: 0.3rem; }
  .add-role select { font-size: 0.7rem; padding: 0.05rem 0.2rem; }
  .muted { color: #6b7280; }
  .error { color: #fca5a5; }
  .notice { color: #6ee7b7; }
</style>
